// Copyright 2025 Sorama B.V.

interface QueuedRequest<T> {
    execute: () => Promise<T>;
    resolve: (value: T) => void;
    reject: (reason: unknown) => void;
}

export interface RequestQueueStatus {
    activeRequests: number;
    queuedRequests: number;
    maxConcurrent: number;
}

/**
 * A generic request queue that manages concurrent execution of async operations
 */
export class RequestQueue {
    private readonly requestQueue: QueuedRequest<unknown>[] = [];
    private activeRequests = 0;
    private readonly maxConcurrentRequests: number;

    constructor(maxConcurrentRequests = 5) {
        this.maxConcurrentRequests = maxConcurrentRequests;
    }

    /**
     * Queues a request to be executed with concurrency control
     */
    public queueRequest<T>(requestExecutor: () => Promise<T>): Promise<T> {
        return new Promise<T>((resolve, reject) => {
            const queuedRequest: QueuedRequest<T> = {
                execute: requestExecutor,
                resolve,
                reject,
            };

            this.requestQueue.push(queuedRequest);
            this.processQueue();
        });
    }

    /**
     * Processes the request queue, maintaining the maximum concurrent request limit
     */
    private async processQueue(): Promise<void> {
        if (this.activeRequests >= this.maxConcurrentRequests || this.requestQueue.length === 0) {
            return;
        }

        const request = this.requestQueue.shift();
        if (!request) {
            return;
        }

        this.activeRequests++;

        try {
            const result = await request.execute();
            request.resolve(result);
        } catch (error) {
            request.reject(error);
        } finally {
            this.activeRequests--;
            // Process next request in queue
            this.processQueue();
        }
    }

    /**
     * Gets the current queue status for monitoring purposes
     */
    public getStatus(): RequestQueueStatus {
        return {
            activeRequests: this.activeRequests,
            queuedRequests: this.requestQueue.length,
            maxConcurrent: this.maxConcurrentRequests,
        };
    }

    /**
     * Clears all pending requests in the queue
     * Note: This does not cancel already executing requests
     */
    public clearQueue(): void {
        // Reject all pending requests
        this.requestQueue.forEach((request) => {
            request.reject(new Error("Queue cleared"));
        });
        this.requestQueue.length = 0;
    }

    /**
     * Waits for all active and queued requests to complete
     */
    public async waitForCompletion(): Promise<void> {
        return new Promise<void>((resolve) => {
            const checkCompletion = () => {
                if (this.activeRequests === 0 && this.requestQueue.length === 0) {
                    resolve();
                } else {
                    setTimeout(checkCompletion, 10);
                }
            };
            checkCompletion();
        });
    }
}
